import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { channelPartnerAuthAPI } from '../../services/api';
import { useChannelPartnerAuth } from '../../contexts/ChannelPartnerAuthContext';
import ThemeToggle from '../../components/UI/ThemeToggle';
import PasswordInput from '../../components/SuperAdmin/PasswordInput';
import './PartnerLogin.scss';

// Forgot-password flow steps (email OTP, same as the superadmin login)
const MODE = {
  LOGIN: 'login',
  FP_EMAIL: 'fp_email', // enter email -> OTP emailed
  FP_OTP: 'fp_otp', // enter the 6-digit OTP
  FP_RESET: 'fp_reset', // set a new password
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PartnerLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useChannelPartnerAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot-password state
  const [mode, setMode] = useState(MODE.LOGIN);
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [info, setInfo] = useState('');

  // Resend OTP countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/partner/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="partner-login">
        <div className="login-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/partner/dashboard');
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ──────────────────────────────────────────────
  const openForgotPassword = () => {
    setMode(MODE.FP_EMAIL);
    setFpEmail(email.trim());
    setFpOtp('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpResetToken('');
    setFpError('');
    setError('');
    setInfo('');
  };

  const backToLogin = () => {
    setMode(MODE.LOGIN);
    setFpError('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    if (!EMAIL_RX.test(fpEmail.trim())) {
      setFpError('Please enter a valid email address');
      return;
    }
    setFpLoading(true);
    try {
      const { data } = await channelPartnerAuthAPI.forgotPassword(fpEmail.trim());
      if (data?.success) {
        toast.success('If this email belongs to a partner, an OTP has been sent to it');
        setMode(MODE.FP_OTP);
        setResendIn(60);
      } else {
        setFpError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendIn > 0 || fpLoading) return;
    setFpError('');
    setFpLoading(true);
    try {
      const { data } = await channelPartnerAuthAPI.forgotPassword(fpEmail.trim());
      if (data?.success) {
        toast.success('A new OTP has been sent');
        setFpOtp('');
        setResendIn(60);
      } else {
        setFpError(data?.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    if (!/^\d{6}$/.test(fpOtp.trim())) {
      setFpError('Please enter the 6-digit OTP from your email');
      return;
    }
    setFpLoading(true);
    try {
      const { data } = await channelPartnerAuthAPI.verifyResetOtp(fpEmail.trim(), fpOtp.trim());
      const token = data?.data?.resetToken;
      if (data?.success && token) {
        setFpResetToken(token);
        setMode(MODE.FP_RESET);
        toast.success('OTP verified - set your new password');
      } else {
        setFpError(data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFpError('');
    if (fpNewPassword.length < 8) {
      setFpError('Password must be at least 8 characters');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match');
      return;
    }
    setFpLoading(true);
    try {
      const { data } = await channelPartnerAuthAPI.resetPassword(fpResetToken, fpNewPassword, fpConfirmPassword);
      if (data?.success) {
        toast.success('Password reset successfully');
        setEmail(fpEmail.trim());
        setPassword('');
        setInfo('Password updated. Sign in with your new password.');
        setMode(MODE.LOGIN);
      } else {
        setFpError(data?.message || 'Failed to reset password.');
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setFpLoading(false);
    }
  };

  const headerText = {
    [MODE.LOGIN]: ['Channel Partner Login', 'Sign in to view your referral link and commission earnings'],
    [MODE.FP_EMAIL]: ['Forgot Password', "Enter your partner email - we'll send you a verification code (OTP)"],
    [MODE.FP_OTP]: ['Verify OTP', `Enter the 6-digit code sent to ${fpEmail.trim() || 'your email'}`],
    [MODE.FP_RESET]: ['Set New Password', 'Choose a new password for your partner account'],
  }[mode];

  return (
    <div className="partner-login">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <div className="login-container">
        <div className="login-header">
          <div className="logo">
            <span className="logo-icon">🤝</span>
            <span className="logo-text">TeleValley Partner</span>
          </div>
          <h1>{headerText[0]}</h1>
          <p>{headerText[1]}</p>
        </div>

        {mode === MODE.LOGIN && (
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          {info && <div className="info-message">{info}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="partner@example.com"
              autoComplete="username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              disabled={loading}
            />
          </div>

          <div className="login-options">
            <button type="button" className="link-button" onClick={openForgotPassword} disabled={loading}>
              Forgot Password?
            </button>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        )}

        {mode === MODE.FP_EMAIL && (
          <form onSubmit={handleSendOtp} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-email">Email</label>
              <input
                type="email"
                id="fp-email"
                value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)}
                placeholder="partner@example.com"
                autoComplete="username"
                maxLength={100}
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={backToLogin} disabled={fpLoading}>
                &larr; Back to Sign In
              </button>
            </div>
          </form>
        )}

        {mode === MODE.FP_OTP && (
          <form onSubmit={handleVerifyOtp} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-otp">Verification Code (OTP)</label>
              <input
                type="text"
                id="fp-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={fpOtp}
                onChange={(e) => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={() => setMode(MODE.FP_EMAIL)} disabled={fpLoading}>
                &larr; Change email
              </button>
              <button type="button" className="link-button" onClick={handleResendOtp} disabled={fpLoading || resendIn > 0}>
                {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
              </button>
            </div>
          </form>
        )}

        {mode === MODE.FP_RESET && (
          <form onSubmit={handleResetPassword} className="login-form" noValidate>
            {fpError && <div className="error-message">{fpError}</div>}
            <div className="form-group">
              <label htmlFor="fp-new-password">New Password</label>
              <PasswordInput
                id="fp-new-password"
                value={fpNewPassword}
                onChange={(e) => setFpNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                maxLength={64}
                required
                disabled={fpLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="fp-confirm-password">Confirm New Password</label>
              <PasswordInput
                id="fp-confirm-password"
                value={fpConfirmPassword}
                onChange={(e) => setFpConfirmPassword(e.target.value)}
                placeholder="Re-enter the new password"
                autoComplete="new-password"
                maxLength={64}
                required
                disabled={fpLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={fpLoading}>
              {fpLoading ? 'Saving...' : 'Reset Password'}
            </button>
            <div className="form-links">
              <button type="button" className="link-button" onClick={backToLogin} disabled={fpLoading}>
                &larr; Back to Sign In
              </button>
            </div>
          </form>
        )}

        <div className="login-footer">
          <p>Contact the platform admin if you don't have login credentials yet.</p>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
